import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Grid,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../../theme";
import superAdminService from "../../../services/SüperAdminServices/superAdminService";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentIcon from "@mui/icons-material/Assignment";
import FunctionsIcon from "@mui/icons-material/Functions";
import FolderIcon from "@mui/icons-material/Folder";
import EmailIcon from "@mui/icons-material/Email";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Tooltip, Legend, XAxis, YAxis, CartesianGrid } from "recharts";
import StatCard from "../../../components/SüperAdminComponents/StatCard";
import WeeklyLineChart from "../../../components/SüperAdminComponents/WeeklyLineChart";

const COLORS = ["#4cceac", "#6870fa", "#db4f4a", "#10b981", "#f59e0b", "#8b5cf6"];

export default function SuperAdminDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);
  const [weeklyTenants, setWeeklyTenants] = useState([]);
  const [weeklyDoctors, setWeeklyDoctors] = useState([]);
  const [weeklyPatients, setWeeklyPatients] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch statistics
      const statistics = await superAdminService.getStatistics();
      setStats(statistics);
      
      // Fetch all data for weekly charts
      // Note: We need to fetch all data without pagination for charts
      // So we set a very high limit to get all records
      const [tenants, doctors, patients] = await Promise.all([
        superAdminService.getAllTenants({ 
          headers: { 
            'x-bone-limit': '10000', 
            'x-bone-pageindex': '0' 
          } 
        }),
        superAdminService.getAllDoctors({ 
          headers: { 
            'x-bone-limit': '10000', 
            'x-bone-pageindex': '0' 
          } 
        }),
        superAdminService.getAllPatients({ 
          headers: { 
            'x-bone-limit': '10000', 
            'x-bone-pageindex': '0' 
          } 
        }),
      ]);

      // Calculate weekly data
      const now = new Date();
      const weeklyData = [];
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        // Count tenants created on this day
        const tenantsCount = tenants.filter(tenant => {
          // Sequelize can return either createdAt (camelCase) or created_at (snake_case)
          const createdDate = tenant.createdAt || tenant.created_at;
          if (!createdDate) return false;
          const tenantDate = new Date(createdDate).toISOString().split('T')[0];
          return tenantDate === dateStr;
        }).length;

        // Count doctors created on this day
        const doctorsCount = doctors.filter(doctor => {
          // Sequelize can return either createdAt (camelCase) or created_at (snake_case)
          const createdDate = doctor.createdAt || doctor.created_at;
          if (!createdDate) return false;
          const doctorDate = new Date(createdDate).toISOString().split('T')[0];
          return doctorDate === dateStr;
        }).length;

        // Count patients created on this day
        const patientsCount = patients.filter(patient => {
          if (!patient.created_at) return false;
          const patientDate = new Date(patient.created_at).toISOString().split('T')[0];
          return patientDate === dateStr;
        }).length;

        weeklyData.push({
          date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          day: date.toLocaleDateString("en-US", { weekday: "short" }),
          tenants: tenantsCount,
          doctors: doctorsCount,
          patients: patientsCount,
        });
      }

      setWeeklyTenants(weeklyData);
      setWeeklyDoctors(weeklyData);
      setWeeklyPatients(weeklyData);
      
      // Prepare pie chart data
      setChartData([
        { name: "Tenants", value: statistics.tenants, color: COLORS[0] },
        { name: "Doctors", value: statistics.doctors, color: COLORS[1] },
        { name: "Patients", value: statistics.patients, color: COLORS[2] },
        { name: "Forms", value: statistics.forms, color: COLORS[3] },
        { name: "Files", value: statistics.files, color: COLORS[4] },
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total for last week
  const getLastWeekTotal = (data, key) => {
    return data.reduce((sum, item) => sum + (item[key] || 0), 0);
  };

  const lastWeekTenants = getLastWeekTotal(weeklyTenants, "tenants");
  const lastWeekDoctors = getLastWeekTotal(weeklyDoctors, "doctors");
  const lastWeekPatients = getLastWeekTotal(weeklyPatients, "patients");

  if (loading && !stats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress sx={{ color: colors.greenAccent[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: "100%", minWidth: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <Typography
        variant="h2"
        sx={{
          mb: 3,
          fontWeight: 700,
          background: `linear-gradient(135deg, ${colors.grey[100]} 0%, ${colors.blueAccent[200]} 100%)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Dashboard Overview
      </Typography>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Tenants"
            value={stats?.tenants}
            icon={<BusinessIcon sx={{ color: COLORS[0], fontSize: 28 }} />}
            color={COLORS[0]}
            subtitle={lastWeekTenants > 0 ? `+${lastWeekTenants} this week` : null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Doctors"
            value={stats?.doctors}
            icon={<PeopleIcon sx={{ color: COLORS[1], fontSize: 28 }} />}
            color={COLORS[1]}
            subtitle={lastWeekDoctors > 0 ? `+${lastWeekDoctors} this week` : null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Patients"
            value={stats?.patients}
            icon={<PersonIcon sx={{ color: COLORS[2], fontSize: 28 }} />}
            color={COLORS[2]}
            subtitle={lastWeekPatients > 0 ? `+${lastWeekPatients} this week` : null}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Forms"
            value={stats?.forms}
            icon={<DescriptionIcon sx={{ color: COLORS[3], fontSize: 28 }} />}
            color={COLORS[3]}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Form Answers"
            value={stats?.formAnswers}
            icon={<AssignmentIcon sx={{ color: COLORS[4], fontSize: 28 }} />}
            color={COLORS[4]}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatCard
            title="Files"
            value={stats?.files}
            icon={<FolderIcon sx={{ color: COLORS[5], fontSize: 28 }} />}
            color={COLORS[5]}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Additional Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Scoring Formulas"
            value={stats?.scoringFormulas}
            icon={<FunctionsIcon sx={{ color: COLORS[0], fontSize: 28 }} />}
            color={COLORS[0]}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Form Invitations"
            value={stats?.formInvitations}
            icon={<EmailIcon sx={{ color: COLORS[2], fontSize: 28 }} />}
            color={COLORS[2]}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Password Resets"
            value={stats?.passwordResets}
            icon={<VpnKeyIcon sx={{ color: COLORS[3], fontSize: 28 }} />}
            color={COLORS[3]}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Weekly Tenants Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                color: colors.grey[100],
                fontWeight: 600,
              }}
            >
              New Tenants (Last 7 Days)
            </Typography>
            <WeeklyLineChart
              data={weeklyTenants}
              dataKey="tenants"
              color={COLORS[0]}
            />
          </Card>
        </Grid>

        {/* Weekly Doctors Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                color: colors.grey[100],
                fontWeight: 600,
              }}
            >
              New Doctors (Last 7 Days)
            </Typography>
            <WeeklyLineChart
              data={weeklyDoctors}
              dataKey="doctors"
              color={COLORS[1]}
            />
          </Card>
        </Grid>

        {/* Weekly Patients Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                color: colors.grey[100],
                fontWeight: 600,
              }}
            >
              New Patients (Last 7 Days)
            </Typography>
            <WeeklyLineChart
              data={weeklyPatients}
              dataKey="patients"
              color={COLORS[2]}
            />
          </Card>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                color: colors.grey[100],
                fontWeight: 600,
              }}
            >
              Data Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[800]}`,
                    borderRadius: "8px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              border: `1px solid ${colors.grey[800]}30`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                color: colors.grey[100],
                fontWeight: 600,
              }}
            >
              System Overview
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[800]} />
                <XAxis
                  dataKey="name"
                  stroke={colors.grey[400]}
                  style={{ fontSize: "12px" }}
                />
                <YAxis stroke={colors.grey[400]} style={{ fontSize: "12px" }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: colors.primary[500],
                    border: `1px solid ${colors.grey[800]}`,
                    borderRadius: "8px",
                    color: colors.grey[100],
                  }}
                />
                <Legend
                  wrapperStyle={{ color: colors.grey[100] }}
                />
                <Bar dataKey="value" fill={colors.greenAccent[500]} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
